/**
 * Route copy for `src/routes/inbox` (keys: `routes.inbox.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  addToCrmButton: {
    lead: 'Lead',
    prospect: 'Prospect',
    client: 'Client',
    influencer: 'Influencer',
    partner: 'Partner',
    couldNotAddToTheCrm: 'Could not add to the CRM. Try again later.',
    addToTheCrm: 'Add to the CRM',
    createACrmContactFromThis: 'Create a CRM contact from this conversation',
    contactName: 'Contact name',
    fullName: 'Full name',
    handleKept: 'Handle kept:',
    lifecycle: 'Lifecycle',
    cancel: 'Cancel',
    adding: 'Adding…',
    addedToTheCrm: '✓ Added to the CRM',
    createdCreated: '{{created}} created',
    existingAlreadyPresent: '{{existing}} already present',
    groupedGrouped: '{{grouped}} grouped',
    addedToTheCrmV: 'Added to the CRM — {{v}}',
    addingToTheCrmFailed: 'Adding to the CRM failed.',
    confirm: 'Confirm',
    theEngagedContactsOfThisConversation: 'The engaged contacts of this conversation will be added to the CRM (deduplicated).',
    targetGroup: 'Target group',
    noGroup: 'No group',
    newGroup: '➕ New group…',
    newGroupName: 'New group name',
    unknown: 'Unknown',
  },
  animationScheduleDetailPage: {
    couldNotLoadTheActivationSchedule: 'Could not load the activation schedule.',
    loading: 'Loading…',
    backToAutomation: '← Back to automation',
    scheduleNotFound: 'Schedule not found.',
    active: 'Active',
    disabled: 'Disabled',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    connection: 'Connection',
    targetReference: 'Target (reference)',
    cadenceRrule: 'Cadence (RRULE)',
    autonomy: 'Autonomy',
    nextRun: 'Next run',
    lastRun: 'Last run',
    neverRun: 'Never run',
    promptTemplate: 'Prompt template',
    lastGeneratedContent: 'Last generated content',
    lastRunDetails: 'Last run details',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  approvalsQueuePage: {
    mostRecent: 'Most recent',
    confidenceRisky: 'Confidence ↑ (risky)',
    confidence: 'Confidence ↓',
    couldNotLoadTheApprovalQueue: 'Could not load the approval queue.',
    approvalQueue: 'Approval queue',
    aiReplyDraftsAwaitingHumanApproval: 'AI reply drafts awaiting human approval.',
    refresh: 'Refresh',
    pending: 'Pending',
    avgConfidence: 'Avg. confidence',
    lowConf: 'Low conf.',
    flagged: 'Flagged',
    sort: 'Sort:',
    loading: 'Loading…',
    noDraftAwaitingApproval: '🎉 No draft awaiting approval.',
    convVV2: 'conv. {{v}}… · {{v2}}',
  },
  automationPage: {
    assignedToAssigneeUserId: 'Assigned to {{assignee_user_id}}',
    tagsV: 'Tags: {{v}}',
    statusV: 'Status → {{v}}',
    priorityV: 'Priority → {{v}}',
    name: 'Name',
    platformScope: 'Platform (scope)',
    keywordsCommaSeparated: 'Keywords (comma-separated)',
    action: 'Action',
    autonomy: 'Autonomy',
    couldNotLoadTheModerationRules: 'Could not load the moderation rules.',
    moderationRules: 'Moderation rules',
    keywordDetectionAndAutomaticActionOn: 'Keyword detection and automatic action on incoming interactions.',
    newRule: '+ New rule',
    newRule2: 'New rule',
    createRule: 'Create rule',
    loading: 'Loading…',
    noModerationRuleYet: 'No moderation rule yet.',
    active: 'Active',
    disabled: 'Disabled',
    keywordsV: 'Keywords: {{v}}',
    noKeyword: 'No keyword',
    view: 'View',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    delete: 'Delete',
    editTheRule: 'Edit the rule',
    save: 'Save',
    deleteTheRule: 'Delete the rule',
    deleteTheRuleNameThisCannot: 'Delete the rule “{{name}}”? This cannot be undone.',
    theNameIsRequired: 'The name is required.',
    theAssignToAnAgentAction: 'The “Assign to an agent” action needs an agent identifier.',
    theAddTagsActionNeedsAt: 'The “Add tags” action needs at least one tag.',
    savingFailed: 'Saving failed.',
    eGFilterSpam: 'E.g. Filter spam',
    allPlatforms: 'All platforms',
    promoScamSpam: 'promo, scam, spam',
    replyTemplate: 'Reply template',
    helloNameThankYouForYour: 'Hello {{name}}, thank you for your message…',
    assignedAgentUserIdentifier: 'Assigned agent (user identifier)',
    userIdentifier: 'User identifier',
    matchingConversationsWillBeAssignedAutom: 'Matching conversations will be assigned automatically to this agent.',
    tagsToAddCommaSeparated: 'Tags to add (comma-separated)',
    vipFollowUpPriority: 'vip, follow-up, priority',
    statusToApply: 'Status to apply',
    priorityToApply: 'Priority to apply',
    ruleActive: 'Rule active',
    cancel: 'Cancel',
    couldNotLoadTheActivationSchedules: 'Could not load the activation schedules.',
    activationSchedules: 'Activation schedules',
    recurringPublicationOfGeneratedContentSi: 'Recurring publication of generated content (simplified RRULE cadence).',
    newSchedule: '+ New schedule',
    newActivationSchedule: 'New activation schedule',
    createTheSchedule: 'Create the schedule',
    noActivationScheduleConfigured: 'No activation schedule configured.',
    draftApprovedAndPublished: 'Draft approved and published.',
    draftApprovedSimulatedPublicationNoReal: 'Draft approved (simulated publication — no real connection).',
    publicationNotPerformedV: 'Publication not performed: {{v}}.',
    theApprovalFailed: 'The approval failed.',
    draftRejected: 'Draft rejected.',
    theRejectionFailed: 'The rejection failed.',
    active2: 'Active',
    disabled2: 'Disabled',
    lastRunV: 'Last run: {{v}}',
    connectionVNextRun: 'Connection {{v}}… · Next run:',
    lastRunV2: ' · Last run {{v}}',
    hideTheDraft: 'Hide the draft',
    viewTheDraft: 'View the draft',
    generatedContentAwaitingReview: 'Generated content — awaiting review',
    contentRejected: 'Content rejected',
    generatedContent: 'Generated content',
    approve: 'Approve',
    reject: 'Reject',
    publishTheDraft: 'Publish the draft',
    publishThisGeneratedContentNowIt: 'Publish this generated content now? It will be sent to the connected platform through the same publishing channel as the automation.',
    publish: 'Publish',
    rejectTheDraft: 'Reject the draft',
    rejectThisDraftItWillNot: 'Reject this draft? It will not be published. The schedule will keep generating content at the next due date.',
    editTheSchedule: 'Edit the schedule',
    deleteTheSchedule: 'Delete the schedule',
    deleteThisActivationScheduleThisCannot: 'Delete this activation schedule? This cannot be undone.',
    connectionCadenceAndPromptTemplateAre: 'Connection, cadence and prompt template are required.',
    connectionIdentifier: 'Connection identifier',
    targetReference: 'Target (reference)',
    pageIdChannelGroup: 'page_id, channel, group…',
    cadenceSimplifiedRrule: 'Cadence (simplified RRULE)',
    freqWeeklyBydayMo: 'FREQ=WEEKLY;BYDAY=MO',
    nextRunOptional: 'Next run (optional)',
    contentType: 'Content type',
    promptTemplate: 'Prompt template',
    writeAnEngagingQuestionForThe: 'Write an engaging question for the community about…',
    scheduleActive: 'Schedule active',
  },
  cannedInsertPanel: {
    couldNotLoadTheRepliesAnd: 'Could not load the replies and macros.',
    thePreviewFailed: 'The preview failed.',
    replyInsertedAsADraft: 'Reply inserted as a draft.',
    theInsertionFailed: 'The insertion failed.',
    macroAppliedSteps: 'Macro applied{{steps}}.',
    applyingTheMacroFailed: 'Applying the macro failed.',
    repliesMacros: 'Replies & macros',
    loading: 'Loading…',
    readOnlyInsertingRepliesAndApplying: '🔒 Read-only — inserting replies and applying macros are not available for your role.',
    savedReply: 'Saved reply',
    noActiveSavedReplyCreateSome: 'No active saved reply. Create some in Community → Replies & macros.',
    choose: '— Choose —',
    emptyRender: '(empty render)',
    preview: 'Preview',
    insertAsADraft: 'Insert as a draft',
    macro: 'Macro',
    noActiveMacroCreateSomeIn: 'No active macro. Create some in Community → Replies & macros.',
    apply: 'Apply',
  },
  confirmDialog: {
    delete: 'Delete',
    cancel: 'Cancel',
  },
  csatPanel: {
    pending: 'Pending',
    couldNotLoadTheCsatSurveys: 'Could not load the CSAT surveys.',
    csatSurveySent: 'CSAT survey sent.',
    csatSurveyCreatedButNotDelivered: 'CSAT survey created but not delivered — no reply channel or email address available. Copy the link to send it yourself.',
    sendingTheSurveyFailed: 'Sending the survey failed.',
    satisfactionCsat: 'Satisfaction (CSAT)',
    loading: 'Loading…',
    tipSendTheSurveyOnceThe: 'Tip: send the survey once the conversation is resolved.',
    questionOptional: 'Question (optional)',
    sendTheSurvey: 'Send the survey',
    readOnlySendingSurveysIsNot: '🔒 Read-only — sending surveys is not available for your role.',
    noSurveySentForThisConversation: 'No survey sent for this conversation.',
    sentOnVV2: 'Sent on {{v}}{{v2}}',
    createdOnV: 'Created on {{v}}',
    answeredOnV: ' · answered on {{v}}',
    notDelivered: 'Not delivered',
    noChannelAddressAvailable: ' — no channel / address available',
    copyTheLink: 'copy the link',
  },
  draftCard: {
    theActionFailedTryAgain: 'The action failed. Try again.',
    aiDraft: '✨ AI draft',
    aiConfidence: 'AI confidence',
    draftText: 'Draft text',
    changesVsTheAiOriginal: 'Changes vs the AI original',
    addition: '＋ addition',
    removal: '－ removal',
    readOnlyApprovalIsReservedFor: '🔒 Read-only — approval is reserved for editors.',
    approveAndSend: 'Approve and send',
    cancel: 'Cancel',
    approve: 'Approve',
    edit: 'Edit',
    reject: 'Reject',
  },
  format: {
    justNow: 'just now',
    sSAgo: '{{s}} s ago',
    mMinAgo: '{{m}} min ago',
    hHAgo: '{{h}} h ago',
    dDAgo: '{{d}} d ago',
    unknown: 'Unknown',
    open: 'Open',
    pending: 'Pending',
    resolved: 'Resolved',
    paused: 'Paused',
    comments: 'Comments',
    privateMessages: 'Private messages',
    mentions: 'Mentions',
    comment: 'Comment',
    reply: 'Reply',
    reaction: 'Reaction',
    approved: 'Approved',
    edited: 'Edited',
    dismissed: 'Dismissed',
    sent: 'Sent',
    sentAuto: 'Sent (auto)',
    hide: 'Hide',
    delete: 'Delete',
    replyDraft: 'Reply draft',
    escalate: 'Escalate',
    assignToAnAgent: 'Assign to an agent',
    addTags: 'Add tags',
    changeTheStatus: 'Change the status',
    setThePriority: 'Set the priority',
    question: 'Question',
    tip: 'Tip',
    draft: 'Draft',
    published: 'Published',
    simulated: 'Simulated',
    notConfigured: 'Not configured',
    platformNotSupported: 'Platform not supported',
    unknownPlatform: 'Unknown platform',
    limitReached: 'Limit reached',
    failed: 'Failed',
    neverRun: 'Never run',
    draftOnly: 'Draft only',
    autonomous: 'Autonomous',
    automatic: 'Automatic',
    poll: 'Poll',
    publishing: 'Publishing',
    flag: 'Flag',
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
    xTwitter: 'X (Twitter)',
    googleReviews: 'Google (reviews)',
  },
  inboxPage: {
    inbox: 'Inbox',
    approvalQueue: 'Approval queue',
    automation: 'Automation',
    socialInbox: 'Social inbox',
    conversationsModerationAndActivationOfYo: 'Conversations, moderation and activation of your multi-platform communities.',
  },
  inboxThreadPage: {
    conversations: 'Conversations',
    open: 'Open',
    pending: 'Pending',
    unread: 'Unread',
    succeededTotalConversationSProcessed: '{{succeeded}}/{{total}} conversation(s) processed.',
    filterByPlatform: 'Filter by platform',
    allPlatforms: 'All platforms',
    filterByStatus: 'Filter by status',
    allStatuses: 'All statuses',
    unanswered: '⏳ Unanswered',
    searchForAContact: 'Search for a contact…',
    search: 'Search',
    assignedTo: 'Assigned to…',
    filterByAssignee: 'Filter by assignee',
    tag: 'Tag…',
    filterByTag: 'Filter by tag',
    filter: 'Filter',
    reset: 'Reset',
    exitSelection: 'Exit selection',
    multipleSelection: '☑ Multiple selection',
    allSize: 'All ({{size}})',
    selected: 'selected',
    resolve: 'Resolve',
    markRead: 'Mark read',
    flag: 'Flag',
    assign: 'Assign…',
    tag2: '+ Tag…',
    bulkChangeTheStatus: 'Bulk change the status',
    status: 'Status…',
    loading: 'Loading…',
    noConversationMatchesTheFilters: 'No conversation matches the filters.',
    totalConversationS: '/ {{total}} conversation(s)',
    selectTheConversationWithName: 'Select the conversation with {{name}}',
    selectAConversationToShowThe: 'Select a conversation to show the thread.',
    loadingTheThread: 'Loading the thread…',
    conversationNotFound: 'Conversation not found.',
    aiDraftSPending: 'AI draft(s) pending',
    noInteractionInThisConversation: 'No interaction in this conversation.',
    otherAiDraftsPending: 'Other AI drafts pending',
    readOnlyYouDoNotHave: 'Read-only — you do not have the rights to reply.',
    replyArea: 'Reply area',
    replySent: 'Reply sent.',
    noIncomingMessageToReplyTo: 'No incoming message to reply to in this thread.',
    noDraftGenerated: 'No draft generated.',
    lengthAiDraftSSuggested: '{{length}} AI draft(s) suggested.',
    couldNotGenerateTheDraft: 'Could not generate the draft.',
    writeAReplyCtrlCmdEnter: 'Write a reply… (Ctrl/Cmd+Enter to send)',
    aiDraftsClickToInsert: 'AI drafts — click to insert',
    escalationRecommended: '⚠ Escalation recommended',
    suggestAiRepliesNothingIsSent: 'Suggest AI replies (nothing is sent automatically)',
    aiWriting: 'AI writing…',
    aiDraft: '✨ AI draft',
    sending: 'Sending…',
    send: 'Send',
    sendingWithinThePlatformSLimits: 'Sending within the platform\'s limits — check the AI drafts before replying.',
    mediacountAttachmentS: '📎 {{mediaCount}} attachment(s)',
    viewOnThePlatform: 'View on the platform ↗',
    conversationDetails: 'Conversation details.',
    theUpdateFailed: 'The update failed.',
    details: 'Details',
    readOnly: '🔒 Read-only',
    sentimentNotAssessed: 'Sentiment not assessed',
    unread2: 'unread',
    aiDraftS: 'AI draft(s)',
    status2: 'Status',
    assignment: 'Assignment',
    userIdentifier: 'User identifier',
    assignToMe: 'Assign to me',
    unassign: 'Unassign',
    unassigned: 'Unassigned',
    tags: 'Tags',
    noTag: 'No tag',
    removeTheTagT: 'Remove the tag {{t}}',
    addATag: 'Add a tag…',
    addATag2: 'Add a tag',
    add: 'Add',
    platform: 'Platform',
    type: 'Type',
    connection: 'Connection',
    lastMessage: 'Last message',
    createdOn: 'Created on',
    assignToUserId: 'Assign to (user ID):',
    tagToAdd: 'Tag to add:',
    priority: 'Priority',
    priorityNotSet: 'Not set',
    priorityPlaceholder: 'Priority…',
    bulkChangeThePriority: 'Bulk change the priority',
  },
  moderationRuleDetailPage: {
    assignedToAssigneeUserId: 'Assigned to {{assignee_user_id}}',
    tagsV: 'Tags: {{v}}',
    statusV: 'Status → {{v}}',
    priorityV: 'Priority → {{v}}',
    couldNotLoadTheModerationRule: 'Could not load the moderation rule.',
    loading: 'Loading…',
    backToAutomation: '← Back to automation',
    ruleNotFound: 'Rule not found.',
    active: 'Active',
    disabled: 'Disabled',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    action: 'Action',
    autonomy: 'Autonomy',
    platformScope: 'Platform (scope)',
    allPlatforms: 'All platforms',
    keywords: 'Keywords',
    noKeyword: 'No keyword',
    replyTemplate: 'Reply template',
    actionParameters: 'Action parameters',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  panelKit: {
    readOnlyAccessYourRoleDoes: 'Read-only access — your role does not allow this action.',
  },
  reengagementPolicyDetailPage: {
    couldNotLoadTheFollowUp: 'Could not load the follow-up policy.',
    loading: 'Loading…',
    backToAutomation: '← Back to automation',
    policyNotFound: 'Policy not found.',
    active: 'Active',
    disabled: 'Disabled',
    realSendingAttemptedSubjectToThe: 'Real sending attempted, subject to the fail-closed CASL compliance gate.',
    eachFollowUpIsFiledAs: 'Each follow-up is filed as a draft awaiting human approval.',
    autoSendUnderCasl: 'Auto send (under CASL)',
    draftOnly: 'Draft only',
    idIdTenantTenantId: 'ID {{id}} · Tenant {{tenant_id}}',
    inactiveFor: 'Inactive for',
    minimumCooldown: 'Minimum cooldown',
    maxPerRun: 'Max per run',
    scope: 'Scope',
    allPlatforms: 'All platforms',
    tagTag: ' · Tag: {{tag}}',
    messageTemplate: 'Message template',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
  },
  reengagementSection: {
    draftPending: 'Draft pending',
    sent: 'Sent',
    simulated: 'Simulated',
    skipped: 'Skipped',
    theNameIsRequired: 'The name is required.',
    theInactivityDelayMustBeBetween: 'The inactivity delay must be between 1 and 8760 hours.',
    theCooldownDelayMustBeBetween: 'The cooldown delay must be between 1 and 8760 hours.',
    theMaximumPerRunMustBe: 'The maximum per run must be between 1 and 500.',
    theMessageTemplateIsRequired: 'The message template is required.',
    savingFailed: 'Saving failed.',
    name: 'Name',
    eGFollowUpSilentCustomers: 'E.g. Follow up silent customers',
    platformScope: 'Platform (scope)',
    allPlatforms: 'All platforms',
    tagScopeOptional: 'Tag (scope, optional)',
    eGVipProspect: 'E.g. vip, prospect…',
    inactiveForHours: 'Inactive for (hours)',
    minimumCooldownHours: 'Minimum cooldown (hours)',
    minimumDelayBeforeFollowingUpThe: 'Minimum delay before following up the same conversation again.',
    maxPerRun: 'Max per run',
    messageTemplate: 'Message template',
    helloNameWeHavenTSeen: 'Hello {name}, we haven\'t seen you recently…',
    theMarker: 'The marker',
    name2: '{name}',
    isReplacedByTheContactS: 'is replaced by the contact\'s display name when it is known.',
    automaticSending: 'Automatic sending',
    offDefaultEachFollowUpIs: 'Off (default): each follow-up is filed as a draft awaiting human approval — nothing is ever sent on its own. On: the follow-up attempts a real send, but only if it passes the same fail-closed CASL/consent compliance gate as any other outgoing message; otherwise it is automatically filed back as a draft.',
    policyActive: 'Policy active',
    cancel: 'Cancel',
    couldNotLoadThePreview: 'Could not load the preview.',
    previewName: 'Preview — {{name}}',
    conversationsThisPolicyWouldCurrentlyHan: 'Conversations this policy would currently handle (inactivity delay:',
    hNoActionIsTakenThis: 'h). No action is taken — this is a preview.',
    loading: 'Loading…',
    noDormantConversationMatchesThisPolicy: 'No dormant conversation matches this policy right now.',
    inactiveForIdleHoursH: '· inactive for {{idle_hours}} h',
    tags: 'Tags:',
    lastMessageV: 'Last message: {{v}}',
    resultPolicyname: 'Result — {{policyName}}',
    processed: 'Processed',
    pending: 'Pending',
    sent2: 'Sent',
    skipped2: 'Skipped',
    noDormantConversationProcessedDuringThis: 'No dormant conversation processed during this run.',
    couldNotLoadTheLog: 'Could not load the log.',
    followUpLog: 'Follow-up log',
    filterByPolicy: 'Filter by policy',
    allPolicies: 'All policies',
    noLogEntryYet: 'No log entry yet.',
    conversationConversationId: 'Conversation: {{conversation_id}}',
    couldNotLoadTheFollowUp: 'Could not load the follow-up policies.',
    dormantConversationFollowUp: 'Dormant conversation follow-up',
    detectsInactiveConversationsAndSuggestsO: 'Detects inactive conversations and suggests (or sends, if allowed) a follow-up.',
    log: 'Log',
    newPolicy: '+ New policy',
    newFollowUpPolicy: 'New follow-up policy',
    createThePolicy: 'Create the policy',
    noFollowUpPolicyConfigured: 'No follow-up policy configured.',
    sentFollowUpSSent: '{{sent}} follow-up(s) sent.',
    queuedDraftSFiledForApproval: '{{queued}} draft(s) filed for approval.',
    noDormantConversationToFollowUp: 'No dormant conversation to follow up right now.',
    executionFailed: 'Execution failed.',
    active: 'Active',
    disabled: 'Disabled',
    inactiveDormantAfterHoursH: 'Inactive ≥ {{dormant_after_hours}} h',
    cooldownCooldownHoursH: 'Cooldown {{cooldown_hours}} h',
    realSendingAttemptedSubjectToThe: 'Real sending attempted, subject to the fail-closed CASL compliance gate.',
    eachFollowUpIsFiledAs: 'Each follow-up is filed as a draft awaiting human approval.',
    autoSendUnderCasl: 'Auto send (under CASL)',
    draftOnly: 'Draft only',
    allConversations: 'All conversations',
    max: '· Max',
    run: '/ run',
    view: 'View',
    preview: 'Preview',
    enableThePolicyToBeAble: 'Enable the policy to be able to run it.',
    run2: 'Run',
    disable: 'Disable',
    enable: 'Enable',
    edit: 'Edit',
    delete: 'Delete',
    runTheFollowUpPolicy: 'Run the follow-up policy',
    runNameNow: 'Run “{{name}}” now?',
    thisPolicySendsAutomaticallyEachFollow: 'This policy sends automatically: each follow-up will attempt a real send, but only if it passes the fail-closed CASL/consent compliance gate — otherwise it will be filed as a draft.',
    eachMatchingDormantConversationWillGet: 'Each matching dormant conversation will get a draft awaiting approval.',
    editThePolicy: 'Edit the policy',
    save: 'Save',
    deleteThePolicy: 'Delete the policy',
    deleteThePolicyNameThisCannot: 'Delete the policy “{{name}}”? This cannot be undone.',
  },
  slaClockPanel: {
    inHuman: 'in {{human}}',
    daysShort: '{{n}} d',
    humanLate: '{{human}} late',
    policyV: 'Policy {{v}}…',
    lengthBreachEs: '⚠ {{length}} breach(es)',
    onTime: 'On time',
    n1stReply: '1st reply',
    resolution: 'Resolution',
    couldNotLoadTheSlaClocks: 'Could not load the SLA clocks.',
    breach: 'Breach',
    sla: 'SLA',
    loading: 'Loading…',
    noActiveSlaClockForThis: 'No active SLA clock for this conversation (no policy applies to it).',
  },
  whatsAppSendPanel: {
    couldNotLoadTheWhatsappWindow: 'Could not load the WhatsApp window.',
    whatsappMessageSent: 'WhatsApp message sent.',
    theWhatsappBusinessApiIsNot: 'The WhatsApp Business API is not configured (no credentials).',
    sendingFailed: 'Sending failed.',
    windowOpen: 'Window open',
    windowClosed: 'Window closed',
    loadingTheWindow: 'Loading the window…',
    no24hWindowOpenNoIncoming: 'No 24h window open — no incoming WhatsApp message has been received yet. Only an approved template can be sent.',
    n24hCustomerWindowOpenExpiresOn: '24h customer window open — expires on {{v}}.',
    n24hWindowClosed: '24h window closed',
    sinceV: ' since {{v}}',
    aMessageTemplateIsRequired: '— a message template is required.',
    readOnlyWhatsappSendingIsNot: '🔒 Read-only — WhatsApp sending is not available for your role.',
    freeMessage: 'Free message',
    yourReply: 'Your reply…',
    model: 'Model',
    outsideTheWindow: '(outside the window)',
    required: '(required)',
    templateLanguage: 'Template language',
    templateVariablesOnePerLine: 'Template variables (one per line)',
    oneVariablePerLine: 'One variable per line',
    send: 'Send',
  },
  widgets: {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    sentimentLabel: 'Sentiment: {{label}}',
    high: 'High',
    confidenceV: 'Confidence {{v}}',
    medium: 'Medium',
    low: 'Low',
  },
};

export default en;
